import { Link } from "react-router-dom";
import { Instagram, Facebook, Youtube, Link2 } from "lucide-react";
import { ASSETS, BRAND, SOCIALS, NAV_LINKS } from "@/lib/brand";

export default function Footer() {
  return (
    <footer
      data-testid="site-footer"
      className="bg-ink text-cream/90 pt-20 pb-10"
    >
      <div className="container-page grid grid-cols-1 md:grid-cols-12 gap-12">
        <div className="md:col-span-5">
          <div className="flex items-center gap-3 mb-6">
            <img
              src={ASSETS.logoWhite}
              alt="A Homely Abode logo"
              className="h-12 w-12 object-contain"
            />
            <div className="leading-tight">
              <div className="font-serif text-2xl text-cream">{BRAND.name}</div>
              <div className="text-[0.7rem] tracking-[0.28em] uppercase text-taupe">
                by {BRAND.founder}
              </div>
            </div>
          </div>
          <p className="font-serif italic text-cream/80 text-xl leading-snug max-w-md">
            Property is one of the greatest tools to accumulate long-term wealth.
            Strong property portfolios can be built with expertise and dedication.
          </p>

          <div className="mt-8 flex items-center gap-3">
            {[
              { href: SOCIALS.instagram, Icon: Instagram, label: "Instagram" },
              { href: SOCIALS.facebook, Icon: Facebook, label: "Facebook" },
              { href: SOCIALS.youtube, Icon: Youtube, label: "YouTube" },
              { href: SOCIALS.linktree, Icon: Link2, label: "Linktree" },
            ].map(({ href, Icon, label }) => (
              <a
                key={label}
                href={href}
                target="_blank"
                rel="noopener noreferrer"
                aria-label={label}
                data-testid={`footer-social-${label.toLowerCase()}`}
                className="inline-flex h-10 w-10 items-center justify-center border border-cream/20 text-cream hover:bg-bronze hover:border-bronze transition-colors"
              >
                <Icon size={16} />
              </a>
            ))}
            <a
              href={SOCIALS.tiktok}
              target="_blank"
              rel="noopener noreferrer"
              aria-label="TikTok"
              data-testid="footer-social-tiktok"
              className="inline-flex h-10 w-10 items-center justify-center border border-cream/20 text-cream hover:bg-bronze hover:border-bronze transition-colors"
            >
              <svg
                viewBox="0 0 24 24"
                width="16"
                height="16"
                fill="currentColor"
                xmlns="http://www.w3.org/2000/svg"
                aria-hidden="true"
              >
                <path d="M19.59 6.69a4.83 4.83 0 0 1-3.77-4.25V2h-3.45v13.67a2.89 2.89 0 0 1-5.2 1.74 2.89 2.89 0 0 1 2.31-4.64 2.93 2.93 0 0 1 .88.13V9.4a6.84 6.84 0 0 0-1-.05A6.33 6.33 0 0 0 5.8 20.1a6.34 6.34 0 0 0 10.86-4.43V8.5a8.16 8.16 0 0 0 4.77 1.52V6.69h-1.84z" />
              </svg>
            </a>
          </div>
        </div>

        <div className="md:col-span-4">
          <h4 className="text-cream font-serif text-xl mb-5">Explore</h4>
          <ul className="space-y-3">
            {NAV_LINKS.map((l) => {
              const idKey = l.to || l.key;
              return (
              <li key={idKey}>
                {l.to ? (
                  <Link
                    to={l.to}
                    data-testid={`footer-link-${l.to.replace(/\//g, "") || "home"}`}
                    className="text-cream/80 hover:text-taupe text-sm tracking-wide transition-colors"
                  >
                    {l.label}
                  </Link>
                ) : (
                  <span className="text-cream/80 text-sm tracking-wide" data-testid={`footer-group-${l.key}`}>
                    {l.label}
                  </span>
                )}
                {l.children && (
                  <ul className="mt-2 ml-3 space-y-2 border-l border-cream/15 pl-3">
                    {l.children.map((c) => (
                      <li key={c.to}>
                        <Link
                          to={c.to}
                          data-testid={`footer-sublink-${c.to.replace(/\//g, "-")}`}
                          className="text-cream/55 hover:text-taupe text-xs tracking-wide transition-colors"
                        >
                          {c.label}
                        </Link>
                      </li>
                    ))}
                  </ul>
                )}
              </li>
              );
            })}
          </ul>
        </div>

        <div className="md:col-span-3">
          <h4 className="text-cream font-serif text-xl mb-5">Connect</h4>
          <ul className="space-y-3 text-sm text-cream/70">
            <li>
              <a
                href={BRAND.whatsapp}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-taupe transition-colors"
                data-testid="footer-whatsapp"
              >
                WhatsApp · {BRAND.phoneDisplay}
              </a>
            </li>
            <li>
              <a
                href={BRAND.valuationLink}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-taupe transition-colors"
                data-testid="footer-valuation"
              >
                Get a Property Valuation →
              </a>
            </li>
            <li>
              <a
                href={SOCIALS.linktree}
                target="_blank"
                rel="noopener noreferrer"
                className="hover:text-taupe transition-colors"
                data-testid="footer-linktree"
              >
                Linktree →
              </a>
            </li>
          </ul>

          <a
            href={BRAND.whatsapp}
            target="_blank"
            rel="noopener noreferrer"
            data-testid="footer-cta-start"
            className="mt-8 inline-flex btn-primary"
          >
            Start Your Property Journey
          </a>
        </div>
      </div>

      <div className="container-page mt-16 pt-8 border-t border-cream/10 flex flex-col md:flex-row justify-between gap-4 text-xs tracking-widest uppercase text-cream/40">
        <span>© {new Date().getFullYear()} A Homely Abode · All rights reserved.</span>
        <div className="flex items-center gap-4">
          <Link to="/privacy" data-testid="footer-privacy" className="hover:text-taupe transition-colors">
            Privacy Policy
          </Link>
          <Link to="/disclaimer" data-testid="footer-disclaimer" className="hover:text-taupe transition-colors">
            Disclaimer
          </Link>
        </div>
        <span>Singapore Real Estate · CEA Regulated</span>
      </div>
    </footer>
  );
}
